import { useAccount } from '@azure/msal-react';
import { useLocalizationContext } from '@im/react-localization';
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FeatureFlagReq,
  FeatureFlagsResponse,
  useFeatureFlag,
} from 'src/hooks/useFeatureFlag';
import { FEATURE_FLAGS } from 'src/util/constants';

interface FeatureFlagContextType {
  error: Error | null;
  featureFlags: FeatureFlagsResponse | null;
  isLoading: boolean;
}

export const FeatureFlagContext = createContext<
  FeatureFlagContextType | undefined
>(undefined);

export const useFeatureFlagsContext = () => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error(
      'useFeatureFlagsContext must be used within a FeatureFlagProvider'
    );
  }
  return context;
};

interface FeatureFlagProviderProps {
  children: ReactNode;
}

export const FeatureFlagProvider = ({ children }: FeatureFlagProviderProps) => {
  const [searchParams] = useSearchParams();
  const l10nContext = useLocalizationContext();
  const { locale } = l10nContext;
  const countryCode = searchParams.get('countryCode') ?? locale.split('-')[1];
  const companyCode = searchParams.get('companyCode');
  const userName = useAccount()?.username.toLowerCase() ?? '';
  const siteCode = countryCode.toLowerCase();

  const request: FeatureFlagReq = useMemo(
    () => ({
      featureflags: FEATURE_FLAGS,
      featurekey: userName.toLocaleLowerCase(),
      rules: {
        'im-companycode': companyCode ?? undefined,
        'im-countrycode': countryCode,
        'im-sitecode': siteCode,
        'im-username': userName,
      },
    }),
    [userName, countryCode, companyCode, siteCode]
  );

  const { data, error, isLoading, mutateAsync } = useFeatureFlag(request);

  // ⬇️ Ensures API is called only once
  const didCallRef = useRef(false);

  useEffect(() => {
    if (!didCallRef.current) {
      didCallRef.current = true;
      mutateAsync(request);
    }
  }, [mutateAsync, request]);

  return (
    <FeatureFlagContext.Provider
      value={{ error, featureFlags: data || null, isLoading }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
};
